'use client';

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from 'react';

import { twMerge } from 'tailwind-merge';

import { searchForSpecies } from '../../../api/rest/searchForSpecies';
import { useRutilusRestQuery } from '../../../api/rest/useRutilusRestQuery';
import { groupSpeciesResults, speciesToSearchOptions } from '../../../helpers/search';
import { ISearchOption } from '../../../interfaces/search';
import { CloseIcon } from '../../Icons/CloseIcon';
import { SearchIcon } from '../../Icons/SearchIcon';
import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner';
import { GroupedOptions } from '../../SearchBox/components/GroupedOptions';
import { ISpeciesInfo } from '../constants';
import { useOnClickOutside } from '../../../helpers/hooks/useOnClickOutside';

interface IProps {
  className?: string;
  onOptionSelect?: Dispatch<SetStateAction<ISpeciesInfo | undefined>>;
}

export const SearchSpecies = ({ className, onOptionSelect }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showResults, setShowResults] = useState(false);
  const [value, setValue] = useState('');

  const { isLoading: isSpeciesFetching, data: speciesResults } = useRutilusRestQuery(
    async (variables, getToken, signal): Promise<ISearchOption[]> => {
      return searchForSpecies(variables.value, getToken, signal).then(({ species }) => {
        return speciesToSearchOptions(species);
      });
    },
    {
      value,
    },
    {},
  );

  const results = groupSpeciesResults(speciesResults || []);

  useEffect(() => {
    if (value) {
      setShowResults(true);
    }
  }, [value]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const onFocus = useCallback(() => {
    setShowResults(true);
  }, [setShowResults]);

  const onClickOutside = useCallback(() => {
    if (showResults) {
      setShowResults(false);
    }
  }, [showResults]);

  useOnClickOutside(containerRef, onClickOutside);

  return (
    <div ref={containerRef} className={twMerge('bg-sea-foam rounded-md', className)}>
      <div className="flex justify-between p-4">
        <div className="flex w-full">
          <label htmlFor="SearchBox" className="mr-3">
            {isSpeciesFetching ? <LoadingSpinner isMini /> : <SearchIcon />}
          </label>
          <input
            ref={searchInputRef}
            className="text-trench w-full"
            type="text"
            autoComplete="off"
            id="SearchBox"
            data-testid="SearchBox"
            aria-label="Search"
            placeholder="Search any species..."
            value={value}
            onChange={onChange}
            onFocus={onFocus}
          />
        </div>

        {showResults && (
          <button
            type="button"
            onClick={() => {
              setShowResults(false);
            }}
            className="mr-0"
          >
            <CloseIcon size={20} />
          </button>
        )}
      </div>
      {showResults && (
        <div className="relative w-full">
          <div className="bg-sea-foam absolute w-full">
            <GroupedOptions
              group={results.speciesList}
              searchTerm={value}
              onCloseSearchBox={() => {
                setShowResults(false);
                setValue('');
              }}
              isFetching={isSpeciesFetching}
              source="homepage" // TODO: https://fishbrain.atlassian.net/browse/FIB-41168
              onOptionSelect={onOptionSelect}
              hideLabel
            />
          </div>
        </div>
      )}
    </div>
  );
};
